import { useCallback, useMemo } from 'react';

import { Address } from 'viem';
import { useReadContracts } from 'wagmi';

import { TokenType, V3_TOKENS_TYPE } from '@constants/vaults/tokens.constants';

import abiCommon from '@abi/vault/PoolV3';
import abiAlgebra from '@abi/vault/PoolV3Algebra';

import { VaultFarmsType } from '@app-types/vaults/vault.types';

interface V3PoolSqrtPriceParamType {
  vaultFarms: VaultFarmsType[];
  isFullInfo?: boolean;
}

export const useV3PoolSqrtPrice = ({
  vaultFarms,
  isFullInfo = false,
}: V3PoolSqrtPriceParamType) => {
  const farmsV3 = useMemo(
    () =>
      vaultFarms.filter((farm) =>
        V3_TOKENS_TYPE.includes(farm.farmInfo.stakingTokenType),
      ),
    [vaultFarms],
  );

  const contractQueriesCommon = useMemo(() => {
    if (!farmsV3) return [];

    // const commonFarmsV3 = farmsV3.filter((item) => item.farmInfo.stakingTokenType !== TokenType.ERC721_ALGEBRA);

    return farmsV3.map((farm) => {
      if (farm.farmInfo.stakingTokenType === TokenType.ERC721_ALGEBRA) {
        return {
          address: farm.farmInfo.poolV3 as Address,
          abi: abiAlgebra,
          functionName: 'globalState' as const,
          chainId: farm.farmInfo.chainId,
        };
      }
      return {
        address: farm.farmInfo.poolV3 as Address,
        abi: abiCommon,
        functionName: 'slot0' as const,
        chainId: farm.farmInfo.chainId,
      };
    });
  }, [farmsV3]);

  const { data: commonData, refetch: refetchCommon } = useReadContracts({
    contracts: contractQueriesCommon,
    allowFailure: false,
    query: {
      enabled: isFullInfo && !!contractQueriesCommon.length,
    },
  });

  const mapSqrtPriceData = useMemo(() => {
    if (!commonData?.length || !farmsV3.length) return {};

    return farmsV3.reduce(
      (acc, farm, index) => {
        const currentRow = commonData[index];

        const sqrtPriceX96 = String(currentRow[0]);

        const { farmId } = farm.farm;

        acc[farmId] = {
          sqrtPriceX96,
        };
        return acc;
      },
      {} as Record<string, { sqrtPriceX96: string }>,
    );
  }, [commonData, farmsV3]);

  const refetch = useCallback(async () => {
    await Promise.all([
      refetchCommon(),
      // refetchAlgebra(),
    ]);
  }, []);

  return {
    refetch,
    sqrtPriceData: mapSqrtPriceData,
  };
};
